import React, { useState, useEffect, useMemo } from 'react';
import { NavBar } from '@/components/navbar';
import { Footer } from '@/components/SiteFooter';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Clock, FileCheck, BarChart3, Shield, Briefcase, Brain, ArrowRight, PenTool, Target, Users, Calendar, CheckCheck, Zap, Star, ArrowDownRight, ArrowUpRight, PlayCircle, ChevronRight, PieChart, LineChart, TrendingUp, Layers, Lock, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ComplianceFormData } from '@/types/compliance';
import ComplianceAIForm from '@/components/compliance-ai/ComplianceAIForm';
import ComplianceAIResponse from '@/components/compliance-ai/ComplianceAIResponse';
import { mockComplianceAiResponse } from '@/mocks/complianceAiResponse';
import { API_CONFIG } from '@/constants/env.ts';
import LoadingAnimation from '@/components/ui/loading-animation';
import { motion } from 'framer-motion';
import PageViewTracker from '@/components/analytics/PageViewTracker';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const TMNGLanding = () => {
  const [formData, setFormData] = useState<ComplianceFormData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [response, setResponse] = useState<any | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(90);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [activeTab, setActiveTab] = useState("process");

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleFormSubmit = async (data: ComplianceFormData) => {
    setFormData(data);
    setIsLoading(true);
    setError(null);
    setTimeRemaining(90);

    try {
      const apiUrl = `${API_CONFIG.API_BASE_URL}/compliance-ai/`;
      console.log('Sending request to compliance AI API:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Origin: window.location.origin,
        },
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(
          `API request failed with status ${response.status}: ${response.statusText}`
        );
      }

      let responseData = await response.json();
      console.log('API Response:', responseData);

      // Process the response
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData);
        } catch (e) {
          responseData = { content: responseData };
        }
      }

      setTimeout(() => {
        setResponse(responseData);
        setIsLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Error submitting form:', error);
      
      if (error instanceof Error) {
        setError(error);
      } else {
        setError(new Error('An unknown error occurred'));
      }

      // Fallback to mock data if API fails
      if (API_CONFIG.USE_MOCK_API) {
        console.log('Falling back to mock data due to API error');
        setTimeout(() => {
          setResponse({
            ...mockComplianceAiResponse,
            title: 'AI-Powered Compliance Assessment for OPEXIA PSF SA',
            summary:
              "Your compliance needs have been analyzed. Here are our tailored recommendations to improve OPEXIA's compliance operations.",
          });
          setIsLoading(false);
        }, 1000);
      } else {
        setIsLoading(false);
      }
    }
  };

  // AI-optimized vs Current Process Comparison Data
  const processComparisonData = useMemo(() => {
    return [
      {
        current: "Manual monitoring of regulatory updates across multiple sources and jurisdictions",
        optimized: "AI-powered continuous monitoring of regulatory sources with automated alerts and impact analysis"
      },
      {
        current: "Team meetings to discuss regulatory changes and determine impact on clients",
        optimized: "Machine learning-based impact assessment providing recommendations for specific actions"
      },
      {
        current: "Manual updates to compliance documentation and procedures based on regulatory changes",
        optimized: "Automated updates to compliance documentation with human review of critical changes"
      },
      {
        current: "Individual client assessment for compliance requirements through manual review",
        optimized: "AI risk profiling of clients with customized compliance requirement generation"
      },
      {
        current: "Document-based evidence collection and manual validation for compliance verification",
        optimized: "Automated evidence collection and validation with exception-based human review"
      },
      {
        current: "Manual creation of compliance reports for internal stakeholders and regulators",
        optimized: "Dynamic compliance dashboards and automated report generation for all stakeholders"
      },
      {
        current: "Reactive addressing of compliance issues as they arise",
        optimized: "Predictive compliance risk identification with proactive intervention suggestions"
      }
    ];
  }, []);

  // AI Optimization Recommendations
  const aiRecommendations = useMemo(() => {
    return [
      {
        icon: <Brain className="h-8 w-8 text-[#1b6504]" />,
        title: "AI-Powered Regulatory Intelligence",
        description: "Automates monitoring of regulatory changes across multiple jurisdictions, providing real-time alerts and impact assessments specific to OPEXIA PSF's business and clients.",
        benefits: ["70% reduction in time spent monitoring regulatory changes", "Improved regulatory prediction capabilities", "Enhanced advisory capability for clients"],
        timeline: "1-3 months"
      },
      {
        icon: <Activity className="h-8 w-8 text-[#1b6504]" />,
        title: "Compliance Workflow Automation",
        description: "Streamlines repetitive compliance tasks, automates document review, and creates intelligent workflows that adapt based on regulatory requirements and client profiles.",
        benefits: ["60% reduction in manual compliance processing time", "Standardized processes across client engagements", "Comprehensive audit trails for all compliance activities"],
        timeline: "3-6 months"
      },
      {
        icon: <Shield className="h-8 w-8 text-[#1b6504]" />,
        title: "AI-Driven Risk Assessment Platform",
        description: "Creates dynamic risk assessments by analyzing patterns across clients, regulations, and historical compliance data to identify emerging risks before they materialize.",
        benefits: ["50% improvement in risk prediction accuracy", "Proactive rather than reactive compliance approach", "Risk-based resource allocation"],
        timeline: "3-6 months"
      },
      {
        icon: <Layers className="h-8 w-8 text-[#1b6504]" />,
        title: "Multi-regulation Compliance Mapping",
        description: "Creates intelligent connections between different regulatory frameworks (CSRD, DORA, MiCAR, SFDR), identifying overlaps and conflicts to optimize compliance activities.",
        benefits: ["40% reduction in duplicative compliance efforts", "Holistic view of regulatory requirements", "Efficient evidence collection serving multiple regulations"],
        timeline: "6-9 months"
      },
      {
        icon: <Users className="h-8 w-8 text-[#1b6504]" />,
        title: "Client Compliance Portal",
        description: "Provides clients with real-time compliance status, automated reporting, and predictive insights, enhancing OPEXIA PSF's service offering while reducing manual client reporting.",
        benefits: ["Enhanced client service differentiation", "60% reduction in client reporting time", "New revenue streams from premium compliance insights"],
        timeline: "9-12 months"
      }
    ];
  }, []);

  // Metrics
  const complianceMetrics = useMemo(() => {
    return [
      { metric: "65%", description: "Reduced compliance costs", icon: <ArrowDownRight className="h-6 w-6" /> },
      { metric: "70%", description: "Regulatory monitoring time saved", icon: <Clock className="h-6 w-6" /> },
      { metric: "60%", description: "Reduction in manual compliance tasks", icon: <CheckCheck className="h-6 w-6" /> },
      { metric: "240+", description: "Hours saved annually", icon: <Calendar className="h-6 w-6" /> }
    ];
  }, []);

  return (
    <div className="min-h-screen flex flex-col font-sans overflow-x-hidden bg-[#FAFAFA]">
      <PageViewTracker pageName="tmng-landing" />
      <Helmet>
        <title>AI-Powered Compliance Transformation for OPEXIA PSF SA | Verdea Enterprise Solutions</title>
        <meta
          name="description"
          content="Tailored compliance solutions for Luxembourg financial services. Reduce costs by 65%, streamline CSSF reporting, and achieve multi-regulatory compliance with AI."
        />
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700;800&display=swap');
          body {
            font-family: 'Work Sans', sans-serif;
          }
        `}</style>
      </Helmet>

      {/* Custom Header - Teamwork Style */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrollPosition > 50 ? 'bg-white shadow-md py-3' : 'bg-transparent py-5'}`}>
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <div className="text-[#1d1c39] font-bold text-2xl mr-8">
                <span className="text-[#1b6504]">Verdea</span>AI
              </div>
              <nav className="hidden md:flex space-x-1">
                <a href="#features" className="px-3 py-2 text-[#1d1c39] hover:text-[#1b6504] font-medium">Features</a>
                <a href="#transformation" className="px-3 py-2 text-[#1d1c39] hover:text-[#1b6504] font-medium">AI Transformation</a>
                <a href="#regulations" className="px-3 py-2 text-[#1d1c39] hover:text-[#1b6504] font-medium">Regulations</a>
                <a href="#recommendations" className="px-3 py-2 text-[#1d1c39] hover:text-[#1b6504] font-medium">Recommendations</a>
              </nav>
            </div>
            <div className="flex items-center space-x-4">
              <a href="#" className="hidden md:inline-block text-[#1d1c39] font-medium hover:text-[#1b6504]">Login</a>
              <Button 
                className="rounded-full bg-[#1b6504] hover:bg-[#184c06] text-white font-medium px-6 py-2 text-sm"
                onClick={() => document.getElementById('request-demo')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      <div className={`h-${scrollPosition > 50 ? '16' : '24'}`}></div>

      <main className="flex-grow">
        {!isLoading && !response && (
          <>
            {/* Hero Section - Tailored for Nordine Garrouche */}
            <section className="relative pt-16 md:pt-20 lg:pt-24 pb-16 md:pb-24 overflow-hidden">
              <div className="absolute inset-0 pointer-events-none z-0">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(24,76,6,0.06)_0%,rgba(255,255,255,0)_70%)]"></div>
                <div className="absolute top-0 right-0 h-[800px] w-[800px] rounded-full blur-3xl opacity-10" 
                  style={{
                    background: 'radial-gradient(circle at center, rgba(27, 101, 4, 0.4) 0%, rgba(27, 101, 4, 0) 70%)',
                    transform: 'translate(20%, -30%)'
                  }}
                ></div>
                <div className="absolute bottom-0 left-0 h-[600px] w-[600px] rounded-full blur-3xl opacity-10" 
                  style={{
                    background: 'radial-gradient(circle at center, rgba(27, 101, 4, 0.3) 0%, rgba(27, 101, 4, 0) 70%)',
                    transform: 'translate(-20%, 30%)'
                  }}
                ></div>
              </div>

              <div className="container mx-auto px-4 md:px-8 relative z-10">
                <div className="flex flex-col-reverse lg:flex-row gap-12 lg:gap-16 items-center">
                  {/* Left side with headline and CTA */}
                  <div className="w-full lg:w-1/2 text-center lg:text-left">
                    <div className="max-w-xl mx-auto lg:mx-0">
                      <div className="inline-flex items-center px-3 py-1 mb-6 rounded-full bg-[#1b6504]/10 text-[#1b6504] text-sm font-medium">
                        <Star className="h-4 w-4 mr-2" /> 
                        Tailored for OPEXIA PSF SA
                      </div>
                      <h1 className="text-[2.75rem] md:text-6xl lg:text-7xl font-extrabold mb-6 tracking-tight leading-[1.1] text-[#1d1c39]">
                        <span className="text-[#1b6504]">Luxembourg</span> compliance <span className="inline-block">reimagined</span>
                      </h1>
                      <p className="text-xl md:text-2xl text-[#1d1c39]/70 mb-8 max-w-lg mx-auto lg:mx-0 leading-relaxed">
                        Nordine, transform OPEXIA's compliance operations with AI-powered solutions that reduce costs by 65% and enhance REGTECH capabilities.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                        <Button 
                          size="lg" 
                          className="bg-[#1b6504] hover:bg-[#184c06] text-white rounded-full px-8 h-14 text-lg font-medium"
                          onClick={() => document.getElementById('request-demo')?.scrollIntoView({ behavior: 'smooth' })}
                        >
                          Request a Consultation
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                        <Button
                          variant="outline"
                          size="lg"
                          className="rounded-full border-[#1d1c39] text-[#1d1c39] hover:bg-[#1d1c39]/5 px-8 h-14 text-lg font-medium"
                        >
                          Watch Demo
                          <PlayCircle className="ml-2 h-5 w-5" />
                        </Button>
                      </div>
                      <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-lg mx-auto lg:mx-0">
                        <div className="flex flex-col items-center lg:items-start">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#1b6504]/10 mb-2">
                            <CheckCircle className="h-5 w-5 text-[#1b6504]" />
                          </div>
                          <span className="text-sm text-[#1d1c39]/70 text-center lg:text-left">CSSF Compliant</span>
                        </div>
                        <div className="flex flex-col items-center lg:items-start">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#1b6504]/10 mb-2">
                            <Clock className="h-5 w-5 text-[#1b6504]" />
                          </div>
                          <span className="text-sm text-[#1d1c39]/70 text-center lg:text-left">70% Faster Regulatory Monitoring</span>
                        </div>
                        <div className="flex flex-col items-center lg:items-start">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#1b6504]/10 mb-2">
                            <FileCheck className="h-5 w-5 text-[#1b6504]" />
                          </div>
                          <span className="text-sm text-[#1d1c39]/70 text-center lg:text-left">Luxembourg-Specific Compliance</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right side with image */}
                  <div className="w-full lg:w-1/2 relative">
                    <div className="relative rounded-2xl overflow-hidden border border-[#1d1c39]/10 shadow-2xl">
                      <img
                        src="/screens/dashboard_overview.png"
                        alt="Compliance Workflow Dashboard for OPEXIA PSF SA"
                        className="w-full h-auto object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1d1c39]/5 to-transparent"></div>
                      
                      {/* Floating badge - top right */}
                      <div className="absolute top-4 right-4 bg-white rounded-full py-2 px-4 shadow-lg flex items-center">
                        <Shield className="h-5 w-5 text-[#1b6504] mr-2" />
                        <span className="text-sm font-medium">CSSF Certified</span>
                      </div>
                      
                      {/* Floating badge - bottom left */}
                      <div className="absolute bottom-4 left-4 bg-white rounded-full py-2 px-4 shadow-lg flex items-center">
                        <ArrowUpRight className="h-5 w-5 text-[#1b6504] mr-2" />
                        <span className="text-sm font-medium">65% Cost Reduction</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Wave divider */}
              <div className="absolute bottom-0 left-0 right-0">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" fill="white" className="w-full h-auto">
                  <path d="M0,96L60,90.7C120,85,240,75,360,69.3C480,64,600,64,720,69.3C840,75,960,85,1080,90.7C1200,96,1320,96,1380,96L1440,96L1440,100L1380,100C1320,100,1200,100,1080,100C960,100,840,100,720,100C600,100,480,100,360,100C240,100,120,100,60,100L0,100Z"></path>
                </svg>
              </div>
            </section>

            {/* Contextual section - Specifically for Nordine and OPEXIA */}
            <section className="py-12 bg-white relative">
              <div className="container mx-auto px-4 md:px-8">
                <div className="max-w-4xl mx-auto">
                  <div className="bg-[#1d1c39]/5 rounded-2xl p-8 md:p-10">
                    <div className="flex flex-col md:flex-row gap-8 items-center">
                      <div className="flex-shrink-0">
                        <div className="w-24 h-24 md:w-32 md:h-32 bg-[#1b6504]/10 rounded-full flex items-center justify-center">
                          <Briefcase className="w-12 h-12 md:w-16 md:h-16 text-[#1b6504]" />
                        </div>
                      </div>
                      <div>
                        <h2 className="text-2xl md:text-3xl font-bold mb-4 text-[#1d1c39]">Built for OPEXIA PSF SA</h2>
                        <p className="text-[#1d1c39]/70 text-lg mb-4">
                          As Luxembourg's leader in REGTECH and Business Process Outsourcing, OPEXIA faces unique compliance challenges with CSSF regulations, evolving directives like CSRD and DORA, and the need for operational resilience.
                        </p>
                        <p className="text-[#1d1c39]/70 text-lg">
                          Our AI solutions are tailored to enhance your existing expertise in financial services, Wealth Management, and Asset Management while addressing your specific compliance needs.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Features Section - Tailored for Luxembourg Financial Services */}
            <section id="features" className="py-24 bg-white">
              <div className="container mx-auto px-4 md:px-8">
                <div className="text-center max-w-3xl mx-auto mb-16">
                  <div className="inline-flex items-center px-3 py-1 mb-6 rounded-full bg-[#1b6504]/10 text-[#1b6504] text-sm font-medium">
                    <Star className="h-4 w-4 mr-2" /> 
                    LUXEMBOURG-SPECIFIC FEATURES
                  </div>
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-[#1d1c39]">Designed for Luxembourg's regulatory landscape</h2>
                  <p className="text-lg md:text-xl text-[#1d1c39]/70 leading-relaxed">
                    Our platform helps OPEXIA PSF SA maintain compliance across multiple regulatory frameworks while enhancing your REGTECH capabilities.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {[
                    {
                      icon: <Brain className="h-8 w-8 text-[#1b6504]" />,
                      title: "CSSF Regulatory Intelligence",
                      description: "Our AI continuously monitors CSSF circulars, guidance, and updates, providing real-time alerts and impact assessments for your client portfolio."
                    },
                    {
                      icon: <Shield className="h-8 w-8 text-[#1b6504]" />,
                      title: "Multi-Regulatory Compliance",
                      description: "Streamline compliance across CSRD, DORA, MiCAR, and SFDR with integrated assessment tools optimized for Luxembourg's implementation requirements."
                    },
                    {
                      icon: <BarChart3 className="h-8 w-8 text-[#1b6504]" />,
                      title: "Automated Compliance Reporting",
                      description: "Generate CSSF-ready reports in minutes rather than days, with built-in validation to ensure accuracy and completeness."
                    },
                    {
                      icon: <Users className="h-8 w-8 text-[#1b6504]" />,
                      title: "Client Portfolio Management",
                      description: "Manage compliance requirements across your 60+ B2B clients with customized risk profiles and automated monitoring."
                    },
                    {
                      icon: <Lock className="h-8 w-8 text-[#1b6504]" />,
                      title: "Operational Resilience Suite",
                      description: "Address CSSF Circular 22/805 requirements with integrated resilience assessment, testing, and reporting tools."
                    },
                    {
                      icon: <Calendar className="h-8 w-8 text-[#1b6504]" />,
                      title: "Regulatory Calendar",
                      description: "Luxembourg-specific compliance calendar with automated reminders for CSSF filings, disclosures, and regulatory deadlines."
                    }
                  ].map((feature, index) => (
                    <div
                      key={index}
                      className="bg-white p-8 rounded-2xl border border-[#1d1c39]/10 hover:border-[#1b6504]/30 hover:shadow-xl transition-all duration-300 group"
                    >
                      <div className="flex items-center justify-center w-14 h-14 rounded-full bg-[#1b6504]/10 mb-6 group-hover:bg-[#1b6504]/20 transition-colors">
                        {feature.icon}
                      </div>
                      <h3 className="text-xl font-bold mb-4 text-[#1d1c39] group-hover:text-[#1b6504] transition-colors">{feature.title}</h3>
                      <p className="text-[#1d1c39]/70">{feature.description}</p>
                      <div className="mt-6 inline-flex items-center font-medium text-[#1b6504] opacity-0 group-hover:opacity-100 transition-opacity">
                        Learn more <ChevronRight className="h-4 w-4 ml-1" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* AI Transformation Analysis Section */}
            <section id="transformation" className="py-24 bg-[#FAFAFA] relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(24,76,6,0.03)_0%,rgba(255,255,255,0)_70%)]"></div>
              <div className="container mx-auto px-4 md:px-8 relative z-10">
                <div className="text-center max-w-3xl mx-auto mb-16">
                  <div className="inline-flex items-center px-3 py-1 mb-6 rounded-full bg-[#1b6504]/10 text-[#1b6504] text-sm font-medium">
                    <Star className="h-4 w-4 mr-2" /> 
                    AI TRANSFORMATION ANALYSIS
                  </div>
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-[#1d1c39]">Transform OPEXIA's compliance operations</h2>
                  <p className="text-lg md:text-xl text-[#1d1c39]/70 leading-relaxed">
                    Our detailed analysis shows how AI can optimize your compliance processes, reduce costs, and enhance your service offerings.
                  </p>
                </div>

                <div className="max-w-5xl mx-auto">
                  <Tabs defaultValue="process" className="w-full" onValueChange={setActiveTab}>
                    <TabsList className="grid w-full grid-cols-1 md:grid-cols-4 mb-12 bg-transparent">
                      <TabsTrigger 
                        value="process" 
                        className={`py-3 ${activeTab === "process" ? "bg-[#1b6504] text-white" : "bg-white text-[#1d1c39]"} rounded-lg border border-[#1d1c39]/10 mx-1 transition-all duration-300`}
                      >
                        Process Comparison
                      </TabsTrigger>
                      <TabsTrigger 
                        value="bottlenecks" 
                        className={`py-3 ${activeTab === "bottlenecks" ? "bg-[#1b6504] text-white" : "bg-white text-[#1d1c39]"} rounded-lg border border-[#1d1c39]/10 mx-1 transition-all duration-300`}
                      >
                        Current Bottlenecks
                      </TabsTrigger>
                      <TabsTrigger 
                        value="benefits" 
                        className={`py-3 ${activeTab === "benefits" ? "bg-[#1b6504] text-white" : "bg-white text-[#1d1c39]"} rounded-lg border border-[#1d1c39]/10 mx-1 transition-all duration-300`}
                      >
                        Business Impact
                      </TabsTrigger>
                      <TabsTrigger 
                        value="timeline" 
                        className={`py-3 ${activeTab === "timeline" ? "bg-[#1b6504] text-white" : "bg-white text-[#1d1c39]"} rounded-lg border border-[#1d1c39]/10 mx-1 transition-all duration-300`}
                      >
                        Implementation Timeline
                      </TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="process" className="mt-4">
                      <div className="bg-white p-8 rounded-2xl shadow-md border border-[#1d1c39]/10">
                        <h3 className="text-2xl font-bold mb-6 text-[#1d1c39]">Current vs. AI-Optimized Process</h3>
                        <div className="space-y-6">
                          {processComparisonData.map((item, index) => (
                            <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 border-b border-[#1d1c39]/10 last:border-0">
                              <div className="bg-[#FAFAFA] p-4 rounded-xl relative">
                                <div className="absolute top-0 right-0 -mt-3 -mr-2">
                                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#1d1c39]/10 text-xs font-bold">
                                    C
                                  </span>
                                </div>
                                <p className="text-[#1d1c39]/80">{item.current}</p>
                              </div>
                              <div className="bg-[#1b6504]/5 p-4 rounded-xl relative">
                                <div className="absolute top-0 right-0 -mt-3 -mr-2">
                                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#1b6504] text-white text-xs font-bold">
                                    AI
                                  </span>
                                </div>
                                <p className="text-[#1d1c39]/80">{item.optimized}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="mt-10 flex justify-center">
                          <div className="grid grid-cols-2 gap-10 max-w-md">
                            <div className="text-center">
                              <div className="text-4xl font-bold text-[#1d1c39] mb-2">120</div>
                              <p className="text-sm text-[#1d1c39]/70">Hours per month<br/>before optimization</p>
                            </div>
                            <div className="text-center">
                              <div className="text-4xl font-bold text-[#1b6504] mb-2">42</div>
                              <p className="text-sm text-[#1d1c39]/70">Hours per month<br/>after optimization</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="bottlenecks" className="mt-4">
                      <div className="bg-white p-8 rounded-2xl shadow-md border border-[#1d1c39]/10">
                        <h3 className="text-2xl font-bold mb-6 text-[#1d1c39]">Current Bottlenecks and Inefficiencies</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {[
                            {
                              icon: <Clock className="h-6 w-6 text-[#1b6504]" />,
                              title: "Manual Compliance Processes",
                              description: "Despite being a REGTECH provider, OPEXIA PSF still relies on manual workflows for new regulations, creating bottlenecks during regulatory changes."
                            },
                            {
                              icon: <Users className="h-6 w-6 text-[#1b6504]" />,
                              title: "Resource Constraints",
                              description: "Recent hiring of junior consultants indicates resource limitations, with expertise gaps creating potential compliance risks."
                            },
                            {
                              icon: <Layers className="h-6 w-6 text-[#1b6504]" />,
                              title: "Cross-regulation Integration",
                              description: "Managing multiple regulatory frameworks (CSRD, DORA, MiCAR, SFDR) creates siloed compliance efforts with duplicative work."
                            },
                            {
                              icon: <Shield className="h-6 w-6 text-[#1b6504]" />,
                              title: "Reactive Approach",
                              description: "Current compliance strategy appears reactive rather than proactive, limiting ability to anticipate regulatory changes."
                            },
                            {
                              icon: <FileCheck className="h-6 w-6 text-[#1b6504]" />,
                              title: "Documentation Consistency",
                              description: "Ensuring uniform documentation across multiple client engagements and regulatory frameworks creates workflow challenges."
                            },
                            {
                              icon: <TrendingUp className="h-6 w-6 text-[#1b6504]" />,
                              title: "Scaling Challenges",
                              description: "With over 350 mandates across 13 countries, scaling consistent compliance is increasingly difficult with current methods."
                            },
                          ].map((item, index) => (
                            <div key={index} className="flex gap-4 p-4 border border-[#1d1c39]/10 rounded-xl">
                              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#1b6504]/10 flex items-center justify-center">
                                {item.icon}
                              </div>
                              <div>
                                <h4 className="font-bold text-[#1d1c39] mb-2">{item.title}</h4>
                                <p className="text-sm text-[#1d1c39]/70">{item.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="benefits" className="mt-4">
                      <div className="bg-white p-8 rounded-2xl shadow-md border border-[#1d1c39]/10">
                        <h3 className="text-2xl font-bold mb-6 text-[#1d1c39]">Business Impact</h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                          {[
                            { metric: "65%", label: "Cost Reduction", description: "Lower operational costs through automation" },
                            { metric: "75%", label: "Accuracy Improvement", description: "Higher compliance documentation accuracy" },
                            { metric: "60%", label: "Risk Reduction", description: "Decreased compliance-related risks" },
                            { metric: "40%", label: "Time Savings", description: "Reduced time for core compliance activities" },
                          ].map((item, index) => (
                            <div key={index} className="bg-[#FAFAFA] p-6 rounded-xl text-center">
                              <div className="text-4xl font-bold text-[#1b6504] mb-2">{item.metric}</div>
                              <div className="font-semibold text-[#1d1c39] mb-2">{item.label}</div>
                              <p className="text-sm text-[#1d1c39]/70">{item.description}</p>
                            </div>
                          ))}
                        </div>
                        
                        <h4 className="text-xl font-bold mb-4 text-[#1d1c39]">Key Performance Metrics</h4>
                        <div className="space-y-4">
                          {[
                            { name: "Time-to-compliance", description: "Duration from regulatory change to full implementation" },
                            { name: "Compliance coverage", description: "Percentage of applicable regulations with automated monitoring and controls" },
                            { name: "Risk prediction accuracy", description: "Success rate of AI system in identifying compliance risks before they materialize" },
                            { name: "Staff efficiency", description: "Compliance tasks completed per staff hour" },
                            { name: "Client satisfaction", description: "Feedback scores on compliance service delivery and reporting quality" },
                          ].map((metric, index) => (
                            <div key={index} className="flex items-center p-3 border-b border-[#1d1c39]/10 last:border-0">
                              <CheckCircle className="h-5 w-5 text-[#1b6504] mr-4" />
                              <div>
                                <span className="font-medium text-[#1d1c39]">{metric.name}:</span>{" "}
                                <span className="text-[#1d1c39]/70">{metric.description}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="timeline" className="mt-4">
                      <div className="bg-white p-8 rounded-2xl shadow-md border border-[#1d1c39]/10">
                        <h3 className="text-2xl font-bold mb-6 text-[#1d1c39]">Implementation Timeline</h3>
                        
                        <div className="relative">
                          {/* Timeline line */}
                          <div className="absolute top-0 bottom-0 left-4 md:left-1/2 w-0.5 bg-[#1b6504]/20 transform -translate-x-1/2"></div>
                          
                          <div className="space-y-12">
                            {[
                              {
                                phase: "Phase 1",
                                months: "Months 1-3",
                                title: "Foundation",
                                items: [
                                  "Regulatory intelligence system implementation",
                                  "Compliance process mapping and baseline establishment",
                                  "Staff AI literacy training",
                                  "Quick-win automation of most repetitive tasks"
                                ]
                              },
                              {
                                phase: "Phase 2",
                                months: "Months 4-6",
                                title: "Core Systems",
                                items: [
                                  "Workflow automation deployment",
                                  "Risk assessment platform implementation",
                                  "Data standardization initiative",
                                  "Initial compliance dashboard development"
                                ]
                              },
                              {
                                phase: "Phase 3",
                                months: "Months 7-9",
                                title: "Advanced Features",
                                items: [
                                  "Multi-regulation compliance mapping",
                                  "Advanced analytics implementation",
                                  "Process refinement based on initial AI insights",
                                  "Expanded automation scope"
                                ]
                              },
                              {
                                phase: "Phase 4",
                                months: "Months 10-12",
                                title: "Optimization",
                                items: [
                                  "Client portal deployment",
                                  "Full integration of all AI compliance systems",
                                  "Advanced predictive capabilities activation",
                                  "Performance measurement and optimization"
                                ]
                              }
                            ].map((phase, index) => (
                              <div key={index} className="relative flex flex-col md:flex-row">
                                <div className="md:w-1/2 md:pr-12 md:text-right mb-8 md:mb-0">
                                  <div className="absolute top-0 left-0 md:left-1/2 w-8 h-8 bg-[#1b6504] rounded-full transform -translate-x-1/2 flex items-center justify-center text-white text-xs font-bold">
                                    {index + 1}
                                  </div>
                                  <h4 className="text-xl font-bold text-[#1b6504]">{phase.phase}</h4>
                                  <div className="text-sm font-medium text-[#1d1c39]/70 mb-2">{phase.months}</div>
                                  <div className="text-lg font-semibold text-[#1d1c39]">{phase.title}</div>
                                </div>
                                <div className="md:w-1/2 md:pl-12">
                                  <ul className="space-y-2">
                                    {phase.items.map((item, itemIndex) => (
                                      <li key={itemIndex} className="flex items-start">
                                        <CheckCircle className="h-5 w-5 text-[#1b6504] mr-2 mt-0.5 flex-shrink-0" />
                                        <span className="text-[#1d1c39]/80">{item}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </div>
            </section>

            {/* Regulations Coverage Section - Specific to Luxembourg */}
            <section id="regulations" className="py-24 bg-white">
              <div className="container mx-auto px-4 md:px-8">
                <div className="max-w-3xl mx-auto">
                  <div className="text-center mb-16">
                    <div className="inline-flex items-center px-3 py-1 mb-6 rounded-full bg-[#1b6504]/10 text-[#1b6504] text-sm font-medium">
                      <Star className="h-4 w-4 mr-2" /> 
                      LUXEMBOURG REGULATORY COVERAGE
                    </div>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-[#1d1c39]">Multi-regulatory compliance</h2>
                    <p className="text-lg md:text-xl text-[#1d1c39]/70 leading-relaxed">
                      Our platform helps OPEXIA PSF SA maintain compliance across Luxembourg's complex regulatory landscape.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { name: "CSSF", description: "Commission de Surveillance du Secteur Financier" },
                      { name: "CSRD", description: "Corporate Sustainability Reporting Directive" },
                      { name: "DORA", description: "Digital Operational Resilience Act" },
                      { name: "MiCAR", description: "Markets in Crypto Asset Regulation" },
                      { name: "SFDR", description: "Sustainable Finance Disclosure Regulation" },
                      { name: "CSDD", description: "Corporate Sustainability Due Diligence" },
                      { name: "NIS2", description: "Network and Information Security Directive" },
                      { name: "GDPR", description: "General Data Protection Regulation" }
                    ].map((regulation, index) => (
                      <div
                        key={index}
                        className="bg-[#FAFAFA] p-5 rounded-2xl border border-[#1d1c39]/10 hover:border-[#1b6504]/30 hover:shadow-lg transition-all duration-300 group"
                      >
                        <h3 className="font-bold text-[#1d1c39] group-hover:text-[#1b6504] transition-colors mb-2">{regulation.name}</h3>
                        <p className="text-sm text-[#1d1c39]/70">{regulation.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Metrics section - OPEXIA specific */}
            <section className="py-24 bg-[#1d1c39] relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(27,101,4,0.15)_0%,rgba(29,28,57,0)_70%)]"></div>
              <div className="container mx-auto px-4 md:px-8 relative z-10">
                <div className="max-w-3xl mx-auto text-center mb-16">
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white">The power of AI for OPEXIA PSF</h2>
                  <p className="text-lg md:text-xl text-white/70 leading-relaxed">
                    See the real impact our platform can deliver for OPEXIA's compliance operations.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {complianceMetrics.map((item, index) => (
                    <div 
                      key={index} 
                      className="relative overflow-hidden rounded-2xl bg-white/10 backdrop-blur-sm p-8 group hover:bg-white/15 transition-colors"
                    >
                      <div className="relative z-10">
                        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#1b6504]/20 mb-6 text-[#1b6504]">
                          {item.icon}
                        </div>
                        <div className="text-5xl lg:text-6xl font-extrabold text-white mb-3">{item.metric}</div>
                        <p className="text-lg text-white/80">{item.description}</p>
                      </div>
                      <div className="absolute top-0 right-0 w-32 h-32 bg-[#1b6504]/10 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* AI Recommendations Section - OPEXIA Specific */}
            <section id="recommendations" className="py-24 bg-[#FAFAFA] relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(24,76,6,0.03)_0%,rgba(255,255,255,0)_70%)]"></div>
              <div className="container mx-auto px-4 md:px-8 relative z-10">
                <div className="text-center max-w-3xl mx-auto mb-16">
                  <div className="inline-flex items-center px-3 py-1 mb-6 rounded-full bg-[#1b6504]/10 text-[#1b6504] text-sm font-medium">
                    <Star className="h-4 w-4 mr-2" /> 
                    AI OPTIMIZATION ROADMAP
                  </div>
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-[#1d1c39]">Strategic AI recommendations</h2>
                  <p className="text-lg md:text-xl text-[#1d1c39]/70 leading-relaxed">
                    Our detailed analysis provides these strategic recommendations for transforming OPEXIA's compliance operations.
                  </p>
                </div>

                <div className="space-y-8 max-w-5xl mx-auto">
                  {aiRecommendations.map((rec, index) => (
                    <div key={index} className="bg-white p-8 rounded-2xl border border-[#1d1c39]/10 shadow-md hover:shadow-xl transition-all duration-300">
                      <div className="flex flex-col md:flex-row gap-6">
                        <div className="flex-shrink-0">
                          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-[#1b6504]/10">
                            {rec.icon}
                          </div>
                        </div>
                        <div className="flex-grow">
                          <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                            <h3 className="text-xl font-bold text-[#1d1c39]">{rec.title}</h3>
                            <Badge className="bg-[#1b6504]/10 text-[#1b6504] hover:bg-[#1b6504]/20 mt-2 md:mt-0">
                              {rec.timeline} implementation
                            </Badge>
                          </div>
                          <p className="text-[#1d1c39]/70 mb-4">{rec.description}</p>
                          <div>
                            <h4 className="font-medium text-[#1d1c39] mb-2">Key Benefits:</h4>
                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {rec.benefits.map((benefit, benefitIndex) => (
                                <li key={benefitIndex} className="flex items-start">
                                  <CheckCircle className="h-5 w-5 text-[#1b6504] mr-2 mt-0.5 flex-shrink-0" />
                                  <span className="text-[#1d1c39]/80 text-sm">{benefit}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Testimonial section */}
            <section className="py-24 bg-white relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(24,76,6,0.03)_0%,rgba(255,255,255,0)_70%)]"></div>
              <div className="container mx-auto px-4 md:px-8 relative z-10">
                <div className="max-w-4xl mx-auto">
                  <div className="bg-[#1d1c39] p-10 md:p-12 rounded-2xl shadow-xl text-white">
                    <div className="text-2xl md:text-3xl font-light italic text-white/90 mb-8 leading-relaxed">
                      "As a Luxembourg-based financial services firm regulated by the CSSF, we faced unique compliance challenges across multiple regulatory frameworks. Verdea AI's platform has allowed us to streamline our compliance operations while enhancing our service offerings to clients."
                    </div>
                    <div className="flex items-center">
                      <div className="h-16 w-16 rounded-full bg-white/20 overflow-hidden mr-6 border-2 border-white/30 shadow-md">
                        <img src="/logos/ejaso.png" alt="Client" className="h-full w-full object-cover" />
                      </div>
                      <div>
                        <div className="font-bold text-xl text-white">Jean-Pierre Lafontaine</div>
                        <div className="text-white/70">Chief Compliance Officer, Luxembourg Financial Services</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Form Section - Tailored to OPEXIA */}
            <section id="request-demo" className="py-24 bg-white relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(24,76,6,0.04)_0%,rgba(255,255,255,0)_70%)]"></div>
              <div className="container mx-auto px-4 md:px-8 relative z-10">
                <div className="max-w-5xl mx-auto">
                  <div className="text-center mb-16">
                    <div className="inline-flex items-center px-3 py-1 mb-6 rounded-full bg-[#1b6504]/10 text-[#1b6504] text-sm font-medium">
                      <Star className="h-4 w-4 mr-2" /> 
                      TAILORED FOR OPEXIA PSF SA
                    </div>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-[#1d1c39]">Request your complimentary compliance analysis</h2>
                    <p className="text-lg md:text-xl text-[#1d1c39]/70 leading-relaxed max-w-2xl mx-auto">
                      Nordine, tell us about OPEXIA's specific compliance challenges, and we'll show you how our AI can transform your operations.
                    </p>
                  </div>
                  
                  <div className="bg-white p-8 md:p-10 rounded-2xl shadow-xl border border-[#1d1c39]/10">
                    <ComplianceAIForm 
                      onSubmit={handleFormSubmit} 
                      isLoading={isLoading}
                      error={error}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Final CTA */}
            <section className="py-20 bg-[#1b6504] text-white relative overflow-hidden">
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-0 right-0 w-[800px] h-[800px] rounded-full blur-3xl opacity-10" 
                  style={{
                    background: 'radial-gradient(circle at center, rgba(255, 255, 255, 0.6) 0%, rgba(255, 255, 255, 0) 70%)',
                    transform: 'translate(30%, -50%)'
                  }}
                ></div>
                <div className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full blur-3xl opacity-10" 
                  style={{
                    background: 'radial-gradient(circle at center, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 70%)',
                    transform: 'translate(-30%, 50%)'
                  }}
                ></div>
              </div>
              
              <div className="container mx-auto px-4 md:px-8 relative z-10">
                <div className="max-w-4xl mx-auto text-center">
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">Transform OPEXIA's Compliance Operations Today</h2>
                  <p className="text-xl opacity-90 mb-10 max-w-2xl mx-auto leading-relaxed">
                    Get your complimentary compliance analysis today and discover how AI can help OPEXIA PSF SA achieve faster, more accurate regulatory compliance across Luxembourg's financial landscape.
                  </p>
                  <Button 
                    size="lg" 
                    className="bg-white text-[#1b6504] hover:bg-white/90 rounded-full px-10 h-14 text-lg font-medium"
                    onClick={() => document.getElementById('request-demo')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    Request Consultation
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </div>
            </section>
          </>
        )}

        {isLoading ? (
          <div className="fixed inset-0 bg-black/10 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto">
                <div className="bg-white p-8 rounded-2xl shadow-xl border border-[#1d1c39]/10">
                  <LoadingAnimation
                    timeRemaining={timeRemaining}
                    setTimeRemaining={setTimeRemaining}
                    initialTime={90}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : response ? (
          <section className="py-24 bg-[#FAFAFA]">
            <div className="container mx-auto px-4 md:px-8">
              <div className="max-w-5xl mx-auto">
                <div className="bg-white p-8 md:p-10 rounded-2xl shadow-xl border border-[#1d1c39]/10">
                  <ComplianceAIResponse
                    response={response}
                    isLoading={isLoading}
                    error={error}
                    onReset={() => {
                      setFormData(null);
                      setResponse(null);
                      setError(null);
                      setTimeRemaining(90);
                    }}
                  />
                </div>
              </div>
            </div>
          </section>
        ) : null}
      </main>

      {/* Footer */}
      {!isLoading && (
        <footer className="bg-[#1d1c39] text-white pt-16 pb-8">
          <div className="container mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-16">
              <div>
                <div className="text-2xl font-bold mb-6">
                  <span className="text-[#1b6504]">Verdea</span>AI
                </div>
                <p className="text-white/70 mb-6">
                  AI-powered compliance platform helping Luxembourg financial services reduce costs, minimize errors, and save time.
                </p>
                <div className="flex space-x-4">
                  <a href="#" className="text-white hover:text-[#1b6504]">
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                    </svg>
                  </a>
                  <a href="#" className="text-white hover:text-[#1b6504]">
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                    </svg>
                  </a>
                  <a href="#" className="text-white hover:text-[#1b6504]">
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                    </svg>
                  </a>
                  <a href="#" className="text-white hover:text-[#1b6504]">
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                    </svg>
                  </a>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-6">Solutions</h3>
                <ul className="space-y-4">
                  <li><a href="#" className="text-white/70 hover:text-white">CSSF Compliance</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">CSRD Compliance</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">NIS2 Compliance</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">Luxembourg Regulatory</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">Custom REGTECH Solutions</a></li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-6">Company</h3>
                <ul className="space-y-4">
                  <li><a href="#" className="text-white/70 hover:text-white">About Us</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">Careers</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">Blog</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">Press</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">Contact</a></li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-6">Resources</h3>
                <ul className="space-y-4">
                  <li><a href="#" className="text-white/70 hover:text-white">Help Center</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">API Documentation</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">Privacy Policy</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">Terms of Service</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">Security</a></li>
                </ul>
              </div>
            </div>

            <div className="border-t border-white/20 pt-8">
              <div className="flex flex-col md:flex-row justify-between items-center">
                <p className="text-white/50 text-sm mb-4 md:mb-0">
                  © 2026 Verdea AI. All rights reserved.
                </p>
                <div className="flex space-x-6">
                  <a href="#" className="text-white/50 hover:text-white text-sm">Privacy Policy</a>
                  <a href="#" className="text-white/50 hover:text-white text-sm">Terms of Service</a>
                  <a href="#" className="text-white/50 hover:text-white text-sm">Cookies</a>
                </div>
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default TMNGLanding;